'use client';
import { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { useAnalytics } from '..';
import type { PageViewedProps } from '../../generated';
import { useConstant } from '../../hooks/useConstant';

export interface AnalyticsPageViewContextValue {
  pageName: string;
  pageViewId: string;
  pageViewReady: boolean;
  contentId?: string | number;
  contentSource?: PageViewedProps['content_source'];
  /** Context-only — not on Page Viewed (Confluence Page Events schema). */
  pageType?: string;
}

const AnalyticsPageViewContext = createContext<AnalyticsPageViewContextValue | null>(null);

export interface PageViewProps {
  name?: string;
  contentId?: string | number;
  contentSource?: PageViewedProps['content_source'];
  pageType?: string;
}

export const AnalyticsPageView = ({
  name = '',
  contentId,
  contentSource,
  children,
  pageType = '',
}: PropsWithChildren<PageViewProps>) => {
  const { analytics, deployableName, deployableVersion, deviceId, gitSha } = useAnalytics();
  const [pageViewReady, setPageViewReady] = useState(false);
  const pageViewId = useConstant(() => crypto.randomUUID());
  useEffect(() => {
    if (!analytics) return;
    // Gate on deviceId so downstream track events share the same ready tick.
    // Page Viewed schema has no traditional_device_id / page_type.
    if (deviceId) {
      const path = window.location.pathname;
      const pageName = name || path;
      const payload: PageViewedProps = {
        deployable_name: deployableName,
        deployable_version: deployableVersion,
        pageview_id: pageViewId,
        name: pageName,
        path,
        referrer: document.referrer,
        search: window.location.search,
        title: document.title,
        url: window.location.href,
        ...(contentId != null ? { content_id: String(contentId) } : {}),
        ...(contentSource ? { content_source: contentSource } : {}),
      };

      analytics.page(pageName, payload, {
        context: {
          app: {
            name: deployableName,
            version: gitSha,
          },
        },
      });
      setPageViewReady(true);
    }
  }, [deviceId]);
  return (
    <AnalyticsPageViewContext.Provider
      value={{
        pageName: name,
        pageViewId: pageViewId,
        pageViewReady,
        contentId,
        contentSource,
        pageType,
      }}
    >
      {children}
    </AnalyticsPageViewContext.Provider>
  );
};

export const useAnalyticsPageView = () => {
  const context = useContext(AnalyticsPageViewContext);
  if (!context) {
    throw new Error('useAnalyticsPageView must be used within a AnalyticsPageView');
  }
  return context;
};

/** Like useAnalyticsPageView, but returns null outside an AnalyticsPageView instead of throwing. */
export const useOptionalAnalyticsPageView = () => useContext(AnalyticsPageViewContext);
